<script setup lang="ts">
import { ref, watch } from "vue";
import { getCSSVar } from "@zyzgroup/core-web";
import Pop from "../pop/Pop.vue";
import Button, { ButtonStylePatternType } from "../form/Button.vue";
// import Icon from "../form/Icon.vue";
import SVGClose from "../svg/SVGClose.vue";
import SVGWarn from "../svg/SVGWarn.vue";
import SVGEllipse from "../svg/SVGEllipse.vue";

// for merge ce shadow-root styles
defineOptions({
  customOptions: { Pop, Button, SVGClose, SVGWarn, SVGEllipse },
  inheritAttrs: true
});

export type DialogStylePatternType =
  | "fullfill"
  | "transparent"
  | "icon-warn-red"
  | "icon-warn-blue";

const props = withDefaults(
  defineProps<{
    title?: string;
    content?: string;
    showClose?: boolean;
    cancelText?: string;
    confirmText?: string;
    closeWhenClickOverlay?: boolean;
    dur?: number;
    minWidth?: string;
    maxWidth?: string;
    overlayBgColor?: string;
    stylePattern?: DialogStylePatternType;
    borderRadius?: string; // border-radius:x/y
  }>(),
  {
    title: "",
    content: "",
    showClose: false,
    cancelText: "",
    confirmText: "",
    closeWhenClickOverlay: false,
    dur: 200,
    minWidth: "280px",
    maxWidth: "320px",
    stylePattern: "fullfill",
    borderRadius: "12px"
  }
);

const emits = defineEmits<{
  (event: "open"): void;
  (event: "close"): void;
  (event: "cancel"): void;
  (event: "confirm"): void;
}>();

const popRef = ref();

const stylePattern = ref<DialogStylePatternType>(props.stylePattern);
const cancelButtonStylePattern = ref<ButtonStylePatternType>("transparent");
const confirmButtonStylePattern = ref<ButtonStylePatternType>("transparent");

const showTopIcon = ref(false);
const colorStart = ref("");
const colorEnd = ref("");

const isBtnsVertical = ref(false);

function setStylePattern(s: DialogStylePatternType) {
  stylePattern.value = s;
  switch (s) {
    case "fullfill":
      cancelButtonStylePattern.value = "fullfill";
      confirmButtonStylePattern.value = "fullfill";
      showTopIcon.value = false;
      colorStart.value = "";
      colorEnd.value = "";
      isBtnsVertical.value = false;
      break;
    case "transparent":
      cancelButtonStylePattern.value = "transparent";
      confirmButtonStylePattern.value = "transparent";
      showTopIcon.value = false;
      colorStart.value = "";
      colorEnd.value = "";
      isBtnsVertical.value = false;
      break;
    case "icon-warn-red":
      cancelButtonStylePattern.value = "transparent";
      confirmButtonStylePattern.value = "fullfill";
      showTopIcon.value = true;
      colorStart.value = getCSSVar("--xg-red", "#fa503c");
      colorEnd.value = getCSSVar("--xg-red-light", "#fa8c78");
      isBtnsVertical.value = true;
      break;
    case "icon-warn-blue":
      cancelButtonStylePattern.value = "transparent";
      confirmButtonStylePattern.value = "fullfill";
      showTopIcon.value = true;
      colorStart.value = getCSSVar("--xg-blue", "#5a82f0");
      colorEnd.value = getCSSVar("--xg-blue-light", "#66b3ff");
      isBtnsVertical.value = true;
      break;
  }
}

watch(
  () => props.stylePattern,
  () => {
    setStylePattern(props.stylePattern);
  },
  { immediate: true }
);

const popOpen = () => {
  emits("open");
};
const popClose = () => {
  emits("close");
  switch (action) {
    case "close":
      emits("cancel");
      break;
    case "confirm":
      emits("confirm");
      break;
  }
};

let action: "open" | "close" | "confirm";

function open() {
  action = "open";
  popRef.value?.open();
}

function close() {
  action = "close";
  popRef.value?.close();
}

function confirm() {
  action = "confirm";
  popRef.value?.close();
}

defineExpose({
  open,
  close
});
</script>

<template>
  <Pop
    ref="popRef"
    directionFrom="center"
    :closeWhenClickOverlay="props.closeWhenClickOverlay"
    :dur="props.dur"
    :overlayBgColor="props.overlayBgColor"
    @open="popOpen"
    @close="popClose"
  >
    <div class="dialog-parent">
      <div
        class="dialog"
        :style="{ minWidth: props.minWidth, maxWidth: props.maxWidth }"
      >
        <!-- 头部提示图片 -->
        <div
          class="top-icon"
          v-if="showTopIcon"
        >
          <div class="top-icon-warn">
            <SVGWarn
              :colorStart="colorStart"
              :colorEnd="colorEnd"
            ></SVGWarn>
          </div>
          <div class="top-icon-warn-shadow"><SVGEllipse></SVGEllipse></div>
        </div>
        <!-- 主体 -->
        <div
          class="dialog-inner"
          :style="{
            paddingTop: showTopIcon ? '24px' : '0px',
            borderRadius: props.borderRadius
          }"
        >
          <!-- 标题 -->
          <div
            class="title"
            v-if="title || showClose"
          >
            <div
              class="title-inner"
              v-if="title"
            >
              {{ title }}
            </div>
            <div
              v-if="showClose"
              class="close"
              @click="close"
            >
              <!-- <Icon icon="ic:baseline-close" color="#cccccc"></Icon> -->
              <div><SVGClose></SVGClose></div>
            </div>
          </div>
          <!-- 内容 -->
          <div class="custom-content">
            <slot>
              <div class="custom-content-default">{{ content }}</div>
            </slot>
          </div>
          <!-- 按钮 -->
          <div
            class="btns"
            :class="[stylePattern, isBtnsVertical ? 'vertical' : '']"
            v-if="cancelText || confirmText"
          >
            <Button
              v-if="cancelText"
              class="btn"
              fontColor="#333333"
              :stylePattern="cancelButtonStylePattern"
              :borderRadius="showTopIcon ? '44px' : '0'"
              :borderColor="showTopIcon ? '#cccccc' : ''"
              :bgColor="
                cancelButtonStylePattern == 'transparent'
                  ? '#ffffff'
                  : '#f5f5f5'
              "
              @click="close"
            >
              {{ cancelText }}
            </Button>
            <Button
              v-if="confirmText"
              class="btn"
              :stylePattern="confirmButtonStylePattern"
              :borderRadius="showTopIcon ? '44px' : '0'"
              :bgColor="
                confirmButtonStylePattern == 'transparent' ? '#ffffff' : ''
              "
              :bgColorStart="colorStart"
              :bgColorEnd="colorEnd"
              :fontWeight="
                confirmButtonStylePattern == 'transparent' ? 'bold' : ''
              "
              @click="confirm"
            >
              {{ confirmText }}
            </Button>
          </div>
        </div>
      </div>
    </div>
  </Pop>
</template>

<style scoped>
.dialog-parent {
  display: flex;
  justify-content: center;
  align-items: center;
}
.dialog {
  position: relative;
}
.top-icon {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
.top-icon-warn {
  width: 60px;
  height: 60px;
}
.top-icon-warn-shadow {
  width: 40px;
  height: 4px;
  margin-top: 4px;
}
.dialog-inner {
  background-color: #ffffff;
  overflow: hidden;
}
.title {
  position: relative;
  font-size: 20px;
  margin-top: 20px;
  color: #333333;
}
.title-inner {
  padding: 0 42px;
  text-align: center;
  font-weight: bold;
}
.close {
  position: absolute;
  top: 0;
  right: 0;
  width: 32px;
  height: 100%;
  text-align: center;
  display: flex;
  justify-content: left;
  align-items: center;
  cursor: pointer;
}
.close > div {
  width: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
}
.custom-content {
  font-size: 16px;
  color: #666666;
  word-break: break-all;
  text-align: center;
}
.custom-content-default {
  padding: 15px 20px 20px;
}
.btns {
  display: flex;
  justify-content: center;
}
.btns.vertical {
  flex-direction: column;
}
.btns.vertical > .btn {
  margin-left: 20px;
  margin-right: 20px;
}
.btns.vertical > .btn:nth-child(1) {
  order: 2;
  margin-top: 10px;
  margin-bottom: 20px;
}
.btns.vertical > .btn:nth-child(2) {
  order: 1;
}
.btns.transparent {
  border-top: 1px solid #cccccc;
}
.btns.transparent > .btn + .btn {
  border-left: 1px solid #cccccc !important;
}
.btn {
  flex: 1 1 auto;
  border-radius: 0;
}
</style>
